
import React, { useState, useEffect } from "react";
import Modal from "@components/modals/Modal";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "@store/slices/modalSlice";
import { assignTranslatorAPI, fetchAllTranslators, fetchKnownLanguages, fetchAssignedFileDetails, fetchAssignedLanguages, fetchAssignedLanguagesToTranslator } from "@store/thunks/translationThunk";
import { getStatusStyle } from "@/utils/constants";

const AssignTranslators = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((store) => store.modal.assignTranslator);
  const fileId = useSelector((store) => store.fileMeta.fileId);
  const translatorAdminId = useSelector((store) => store.auth?.userId);
  const { assignedLanguages, assignedLanguagesToUser } = useSelector((store) => store.translatorAdmin);

  const [assignments, setAssignments] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [knownLanguages, setKnownLanguages] = useState([]);
  const [languageTranslators, setLanguageTranslators] = useState({});
  const [currentAssignments, setCurrentAssignments] = useState([]);

  const handleClose = () => {
    dispatch(closeModal("assignTranslator"));
    setAssignments({});
    setShowConfirmation(false);
  };

  useEffect(() => {
    if (isOpen && fileId) {
      dispatch(fetchAssignedLanguages(fileId));
      dispatch(fetchAssignedLanguagesToTranslator(fileId));
    }
  }, [dispatch, fileId, isOpen]);

  useEffect(() => {
    if (isOpen && translatorAdminId && fileId) {
      dispatch(fetchKnownLanguages(translatorAdminId)).then((res) => {
        const languages = res.data || [];
        setKnownLanguages(languages);
        languages.forEach((lang) => {
          dispatch(fetchAllTranslators(lang.languageId)).then((translatorRes) => {
            setLanguageTranslators((prev) => ({
              ...prev,
              [lang.languageId]: translatorRes.data || [],
            }));
          });
        });
      }).catch((error) => {
        console.error("Error fetching known languages:", error);
      });

      // dispatch(fetchAssignedFileDetails({ fileId, translatorUserId: translatorAdminId }))
      //   .then((resultAction) => {
      //     if (fetchAssignedFileDetails.fulfilled.match(resultAction)) {
      //       setCurrentAssignments(resultAction.payload || []);
      //     }
      //   })
      //   .catch((error) => {
      //     console.error("Error fetching current assignments:", error);
      //   });
    }
  }, [isOpen, translatorAdminId, fileId, dispatch]);

  const assignTranslator = (languageId, translatorId) => {
  setAssignments((prev) => {
    if (!translatorId) {
      // ❌ If empty, remove from assignments
      const { [languageId]: _, ...rest } = prev;
      return rest;
    }
    // ✅ Otherwise, add/update assignment
    return {
      ...prev,
      [languageId]: translatorId,
    };
  });
};


  const handleSubmit = (e) => {
    e.preventDefault();
    if (Object.keys(assignments).length > 0) {
      setShowConfirmation(true);
    } else {
      alert("Please assign at least one translator.");
    }
  };

  const generateResult = () => {
    const res = Object.keys(assignments).map((langId) => {
      const translatorsArr = languageTranslators[langId] || [];
      const translator = translatorsArr.find((t) => String(t.userId) === String(assignments[langId]));
      const language = knownLanguages.find((l) => l.languageId === parseInt(langId));
      const sourceLanguageId = language?.sourceLanguageId || 2;
      const targetLanguageId = parseInt(langId);
      const translatorUserId = translator?.userId;
      return {
        fileId,
        sourceLanguageId,
        targetLanguageId,
        translatorUserId,
        translatorAdminId: parseInt(translatorAdminId),
      };
    });
    return res;
  };

  const validateAssignment = (assignments) => {

    // If assignments is not an array or is empty, show error and return false
    if (!Array.isArray(assignments) || assignments.length === 0) {
      alert("No valid assignments provided.");
      return false;
    }

    let allValid = true;
    const errors = [];

    assignments.forEach((assignment, index) => {
      // Check for required fields
      if (
        !assignment.fileId ||
        !assignment.sourceLanguageId ||
        !assignment.targetLanguageId ||
        !assignment.translatorUserId ||
        !assignment.translatorAdminId
      ) {
        errors.push(`Assignment at index ${index}: All fields are required for assignment.`);
        allValid = false;
      }

      // Check if source and target languages are the same
      if (assignment.sourceLanguageId === assignment.targetLanguageId) {
        errors.push(`Assignment at index ${index}: Source and target languages cannot be the same.`);
        allValid = false;
      }
    });

    // Show all errors in a single alert
    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    return allValid;
  };


  const noLanguagesAvailable = knownLanguages.length === 0 || assignedLanguages.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Assign File to Translators"
      maxWidth="7xl"
    >
      <div className="flex flex-col h-full max-h-[90vh] bg-white">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 z-10">
          <h2 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
            Assign File to Translators
          </h2>
          <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">
            Select translators for each language
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4">
          {noLanguagesAvailable ? (
            <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                All languages have already been assigned to this file.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {assignedLanguages.length > 0 && (
                <p className="text-sm text-gray-700 font-['Semantic/Strong'] mb-4">
                  Assigned languages to admin: <span className="text-[#ec7026] font-bold">{assignedLanguages.map(lang => lang.name).join(", ")}</span>
                </p>
              )}
              {assignedLanguages.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-gray-500 font-['Semantic/Strong']">
                    No assigned languages to admin found.
                  </td>
                </tr>
              ) : (
                <table className="min-w-full border border-gray-200">
                  {assignedLanguages
                    .filter(
                      (lang) => !assignedLanguagesToUser.some((assigned) => assigned.languageId === lang.languageId)
                    ).length > 0 && (
                      <thead className="bg-gray-50">
                        <tr className="text-sm text-gray-900 font-['Semantic/Strong']">
                          <th className="border border-gray-200 px-4 py-2 text-left">Language</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Translator</th>
                          <th className="border border-gray-200 px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                    )
                  }
                  <tbody className="text-sm text-gray-700 font-['Semantic/Strong']">
                    {assignedLanguages
                      .filter(
                        (lang) => !assignedLanguagesToUser.some((assigned) => assigned.languageId === lang.languageId)
                      ).length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-4 text-gray-500 font-['Semantic/Strong']">
                          All languages have been assigned.
                        </td>
                      </tr>
                    ) : (
                      assignedLanguages
                        .filter(
                          (lang) => !assignedLanguagesToUser.some((assigned) => assigned.languageId === lang.languageId)
                        )
                        .map((lang) => (
                          <tr key={lang.languageId} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="border border-gray-200 px-4 py-2">{lang.name || "N/A"}</td>
                            <td className="border border-gray-200 px-4 py-2">
                              <select
                                value={assignments[lang.languageId] || ""}
                                onChange={(e) => assignTranslator(lang.languageId, e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                              >
                                <option value="">Select a translator</option>
                                {(languageTranslators[lang.languageId] || []).map((translator) => (
                                  <option key={translator.userId} value={translator.userId}>
                                    {translator.userName}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="border border-gray-200 px-4 py-2">
                              <span className={`px-2 py-1 rounded-full text-sm ${getStatusStyle(assignments[lang.languageId] ? "ASSIGNED" : "NOT_ASSIGNED")}`}>
                                {assignments[lang.languageId] ? "ASSIGNED" : "NOT_ASSIGNED"}
                              </span>
                            </td>
                          </tr>
                        )))}

                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 bg-white">
          {Object.keys(assignments).length > 0 && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-[#ec7026]">
              <h3 className="text-sm font-['Semantic/Strong'] font-bold text-gray-900 mb-2">New Assignment Summary</h3>
              <p className="text-sm text-gray-600 font-['Semantic/Strong'] mb-2">
                {Object.keys(assignments).length} language(s) assigned
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(assignments).map((langId) => {
                  const language = knownLanguages.find((l) => l.languageId === parseInt(langId));
                  return (
                    <span
                      key={langId}
                      className="px-2 py-1 rounded-full text-sm bg-[#ec7026] text-white font-['Semantic/Strong']"
                    >
                      {language?.name || "N/A"}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleSubmit}
              disabled={Object.keys(assignments).length === 0 || noLanguagesAvailable}
              className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white transition-colors duration-200 ${Object.keys(assignments).length === 0 || noLanguagesAvailable
                ? "border-gray-300 bg-gray-200 text-gray-500 cursor-not-allowed"
                : "border-[#ec7026] bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
                }`}
            >
              Assign Translators
            </button>
          </div>
        </div>
        {showConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-200">
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-[calc(100vw-2rem)] sm:w-96">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4">
                <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">Confirm Assignment</h2>
                <p className="text-sm text-gray-500 font-['Semantic/Strong'] mt-1">Review and confirm translator assignments</p>
              </div>
              <div className="px-4 sm:px-6 lg:px-8 py-4">
                <p className="text-sm text-gray-700 font-['Semantic/Strong'] mb-2">File ID: {fileId}</p>
                <div className="max-h-64 overflow-y-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr className="text-sm text-gray-900 font-['Semantic/Strong']">
                        <th className="border border-gray-200 px-4 py-2 text-left">Language</th>
                        <th className="border border-gray-200 px-4 py-2 text-left">Translator</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700 font-['Semantic/Strong']">
                      {Object.keys(assignments).map((langId) => {
                        const translatorsArr = languageTranslators[langId] || [];
                        const translator = translatorsArr.find((t) => String(t.userId) === String(assignments[langId]));
                        const language = knownLanguages.find((l) => l.languageId === parseInt(langId));
                        return (
                          <tr key={langId} className="hover:bg-gray-50 transition-colors duration-200">
                            <td className="border border-gray-200 px-4 py-2">{language?.name || "N/A"}</td>
                            <td className="border border-gray-200 px-4 py-2">{translator ? translator.userName : "Not found"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="px-4 sm:px-6 lg:px-8 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const result = generateResult();
                    if (validateAssignment(result)) {
                      dispatch(assignTranslatorAPI(result));
                      setShowConfirmation(false);
                      setAssignments({});
                      handleClose();
                    }
                  }}
                  className="inline-flex items-center px-3 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AssignTranslators;